import React from 'react';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import GavelIcon from '@mui/icons-material/Gavel';
import ScaleIcon from '@mui/icons-material/LibraryBooks';
import VerifiedIcon from '@mui/icons-material/Verified';
import SpeedIcon from '@mui/icons-material/Speed';
import { useLanguage } from './LanguageProvider';
import logo from '../Images/Almaryah-Legal-Consultancy.png';

const HeroSection = () => {
  const { currentLang: language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const content = {
    en: {
      title: "Your Trusted Partner in",
      highlight: "Legal Solutions",
      subtitle: "Expert legal consultancy and administrative support services in the UAE. We provide professional guidance to protect your rights and facilitate your success.",
      cta1: "Get Free Consultation",
      cta2: "Explore Services",
      stats: [
        { number: "500+", label: "Successful Cases", icon: GavelIcon },
        { number: "15+", label: "Years Experience", icon: VerifiedIcon },
        { number: "98%", label: "Success Rate", icon: ScaleIcon },
        { number: "24/7", label: "Support", icon: SpeedIcon }
      ]
    },
    ar: {
      title: "شريكك الموثوق في",
      highlight: "الحلول القانونية",
      subtitle: "استشارات قانونية متخصصة وخدمات دعم إداري في الإمارات. نقدم التوجيه المهني لحماية حقوقك وتسهيل نجاحك.",
      cta1: "احصل على استشارة مجانية",
      cta2: "استعرض الخدمات",
      stats: [
        { number: "٥٠٠+", label: "قضية ناجحة", icon: GavelIcon },
        { number: "١٥+", label: "سنوات خبرة", icon: VerifiedIcon },
        { number: "٩٨٪", label: "نسبة نجاح", icon: ScaleIcon },
        { number: "٢٤/٧", label: "دعم متواصل", icon: SpeedIcon }
      ]
    }
  };

  const currentContent = content[language];

  const handleNavigation = (path) => {
    if (path === '/#contact') {
      if (location.pathname === '/') {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/');
        setTimeout(() => {
          const contactSection = document.getElementById('contact');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    } else {
      navigate(path);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  const floatingAnimation = {
    y: [0, -20, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const logoAnimation = {
    scale: [1, 1.05, 1],
    rotate: [0, 2, -2, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <Box
      id="home"
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0b0c1e 0%, #1a1b2f 100%)',
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 12, md: 0 },
        display: 'flex',
        alignItems: 'center',
        color: 'white',
        direction: isRTL ? 'rtl' : 'ltr',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: { xs: '80px', sm: '100px', md: '150px' },
          background: 'white',
          borderRadius: '50% 50% 0 0',
          transform: 'translateY(50%)',
          zIndex: 20000
        }
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: 'absolute',
          top: '10%',
          right: isRTL ? 'auto' : '5%',
          left: isRTL ? '5%' : 'auto',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0) 70%)',
          zIndex: 0
        }}
      />

      <motion.div
        animate={floatingAnimation}
        style={{
          position: 'absolute',
          bottom: '10%',
          left: isRTL ? 'auto' : '5%',
          right: isRTL ? '5%' : 'auto',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0) 70%)',
          zIndex: 0
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Content */}
          <Grid item xs={12} md={7}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontFamily: language === 'ar' ? 'Cairo' : 'Inter',
                    fontWeight: 'bold',
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.2,
                    mb: 2,
                    color: 'white',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                >
                  {currentContent.title}{' '}
                  <Box
                    component="span"
                    sx={{
                      color: '#d4af37',
                      display: 'inline-block'
                    }}
                  >
                    {currentContent.highlight}
                  </Box>
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: language === 'ar' ? 'Cairo' : 'Inter',
                    mb: 4,
                    color: 'rgba(255,255,255,0.8)',
                    lineHeight: 1.8,
                    fontSize: { xs: '1rem', md: '1.2rem' },
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                >
                  {currentContent.subtitle}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  flexWrap: 'wrap', 
                  mb: 6,
                  justifyContent: isRTL ? 'flex-start' : 'flex-start'
                }}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => handleNavigation('/#contact')}
                      sx={{
                        background: '#d4af37',
                        color: '#0b0c1e',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontFamily: language === 'ar' ? 'Cairo' : 'Inter',
                        fontWeight: 600,
                        borderRadius: '50px',
                        boxShadow: '0 8px 20px rgba(212, 175, 55, 0.3)',
                        '&:hover': {
                          background: '#c4a030',
                          boxShadow: '0 12px 25px rgba(212, 175, 55, 0.4)'
                        }
                      }}
                    >
                      {currentContent.cta1}
                    </Button>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => handleNavigation('/services')}
                      sx={{
                        color: '#d4af37',
                        borderColor: '#d4af37',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontFamily: language === 'ar' ? 'Cairo' : 'Inter',
                        fontWeight: 600,
                        borderRadius: '50px',
                        '&:hover': {
                          borderColor: '#c4a030',
                          backgroundColor: 'rgba(212, 175, 55, 0.1)',
                          color: '#c4a030'
                        }
                      }}
                    >
                      {currentContent.cta2}
                    </Button>
                  </motion.div>
                </Box>
              </motion.div>

              {/* Stats */}
              <motion.div variants={itemVariants}>
                <Grid container spacing={3}>
                  {currentContent.stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <Grid item xs={6} md={3} key={index}>
                        <motion.div
                          whileHover={{ y: -10 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <Box
                            sx={{
                              textAlign: 'center',
                              p: 2,
                              background: 'rgba(255,255,255,0.05)',
                              borderRadius: '15px',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(212, 175, 55, 0.2)'
                            }}
                          >
                            <Icon sx={{ fontSize: 40, mb: 1, color: '#d4af37' }} />
                            <Typography
                              variant="h5"
                              sx={{
                                fontFamily: language === 'ar' ? 'Cairo' : 'Inter',
                                fontWeight: 'bold',
                                color: 'white'
                              }}
                            >
                              {stat.number}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: language === 'ar' ? 'Cairo' : 'Inter',
                                color: 'rgba(255,255,255,0.7)'
                              }}
                            >
                              {stat.label}
                            </Typography>
                          </Box>
                        </motion.div>
                      </Grid>
                    );
                  })}
                </Grid>
              </motion.div>
            </motion.div>
          </Grid>

          {/* Logo */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: { xs: 300, md: 500 },
                  position: 'relative'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: { xs: 280, md: 400 },
                    height: { xs: 280, md: 400 },
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(212,175,55,0.2) 0%, rgba(212,175,55,0) 70%)',
                    animation: 'pulse 4s ease-in-out infinite'
                  }}
                />
                
                <motion.div
                  animate={logoAnimation}
                  style={{
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="Almaryah Legal Consultancy | الماريه للإستشارات القانونيه"
                    sx={{
                      width: { xs: 250, md: 380 },
                      height: 'auto',
                      maxWidth: '100%',
                      filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))',
                      borderRadius: '20px',
                      border: '3px solid #d4af37'
                    }}
                  />
                </motion.div>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
            100% { transform: translateY(0px); }
          }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.2; }
            50% { transform: scale(1.1); opacity: 0.3; }
            100% { transform: scale(1); opacity: 0.2; }
          }
        `}
      </style>
    </Box>
  );
};

export default HeroSection;